<script setup lang="ts">
// icons
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons';

// validation
import { defineRule, useField } from 'vee-validate';
import { required } from '@vee-validate/rules';

defineRule('required', required);
defineRule('notRequiredIfUrl', (_value, [target], ctx) => {
  return !!ctx.form[target];
});

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiVideoUploadElementProps {
  campsite?: any;
  disabled?: boolean;
  errors?: any;
  index?: number;
  showRemoveButton: boolean;
  form: any;
}
const props = withDefaults(defineProps<CiVideoUploadElementProps>(), {
  campsite: () => {},
  disabled: false,
  errors: () => {},
  index: 0,
});

const emit = defineEmits<{
  input: [value: any];
  loading: [value: boolean];
  remove: [];
}>();

/********************
 * COMPOSITIONS      *
 ********************/
const videoService = useVideoService();
const { $gettext } = useGettext();
const { closeModal } = useCiModal();
const { locale } = useI18n();
const { openModalError } = useModalDialog({ campsite: () => props.campsite });

/********************
 * REFS & VARS       *
 ********************/
const form = reactive({ ...props.form });
const image = ref('https://img.youtube.com/vi/0.jpg');
const isPreviewImagePending = ref(false);
const isUpload = ref(false);
const loadingAttempts = ref(0);
const vimeoUploading = ref(false);

const { value: urlValue, errorMessage: urlError } = useField<string>(`video_url_${props.index}`, 'required');
const { value: dropzoneValue, errorMessage: dropzoneError } = useField<string>(
  `video_upload_${props.index}`,
  `notRequiredIfUrl:video_url_${props.index}`,
);
const { value: titleValue, errorMessage: titleError } = useField<string>(`video_title_${props.index}`, 'required');

const videoUrlLabel = computed(() => {
  return $gettext('Link (Youtube or Vimeo)');
});

const videoUrlPlaceholder = computed(() => {
  return $gettext('e.g. https://www.youtube.com/watch?v=...');
});

const titleLabel = computed(() => {
  return $gettext('Title');
});

const titlePlaceholder = computed(() => {
  return $gettext('Heading (max. 255 characters)');
});

const descriptionLabel = computed(() => {
  return $gettext('Description');
});

const descriptionPlaceholder = computed(() => {
  return $gettext('Describe the video here (max. 10000 characters)');
});

const getErrors = computed(() => {
  if (!props.errors) {
    return '';
  }
  const errors = [];
  Object.keys(props.errors).forEach((field) => {
    const err = props.errors[field][0];
    errors.push(err.message);
  });
  return errors.join(' ');
});

/********************
 * WATCHER           *
 ********************/
watch(() => form, onFromChange, { deep: true });
watch([urlValue, dropzoneValue, titleValue], ([newUrl, newDropzoneValue, newTitle]) => {
  form.title = newTitle;
  form.video_url = newUrl;
  form.uploaded_via_ci = !!newDropzoneValue;
  onFromChange(form);
});

/********************
 * FUNCTIONS         *
 ********************/
function onFromChange(newValue) {
  emit('input', newValue);
}

function getPreview(ev) {
  getPreviewImageFromUrl(ev.target.value);
}

function getPreviewImageFromUrl(url) {
  let timeout;
  isPreviewImagePending.value = true;

  videoService
    .getPreview(url, locale.value)
    .then((result) => {
      clearTimeout(timeout);

      if (!result && loadingAttempts.value < 60) {
        timeout = setTimeout(() => {
          loadingAttempts.value += 1;
          getPreviewImageFromUrl(url);
        }, 2500);
      } else {
        isPreviewImagePending.value = false;
        image.value = result;
      }
    })
    .catch(() => {
      isPreviewImagePending.value = false;
    });
}

function onVideoSelected(videos) {
  const vids = [...videos];
  vimeoUploading.value = true;
  emit('loading', true);

  videoService
    .createVideo(locale.value, { size: vids[0].size })
    .then((response) => {
      videoService
        .uploadVideo(response.form_url, locale.value, vids[0])
        .then(() => {
          form.uploaded_via_ci = true;
          vimeoUploading.value = false;
          isUpload.value = true;
          const url = `https://vimeo.com/${response.video_id}`;
          dropzoneValue.value = url;
          urlValue.value = url;
          // form.video_url = url;
          form.embed_param_h = response.embed_param_h || null;
          getPreviewImageFromUrl(url);
          emit('loading', false);
        })
        .catch(() => {
          uploadError();
        });
    })
    .catch(() => {
      uploadError();
    });
}

function uploadError() {
  emit('loading', false);
  isUpload.value = false;
  vimeoUploading.value = false;

  const buttons = [
    {
      label: $gettext('Close'),
      variant: 'text',
      onClick: () => {
        closeModal('CiModalError');
      },
    },
  ];

  openModalError(
    buttons,
    $gettext('Upload failed'),
    $gettext('We are sorry but your upload failed. Please try again.'),
  );
}
</script>

<template>
  <transition
    name="slide-fade"
    appear
  >
    <div class="container mt-6 rounded-md border border-dashed border-black pt-4">
      <div class="row">
        <div class="col-12 col-md-3 mb-6 md:mb-0">
          <div class="flex h-full items-center justify-center">
            <img
              v-if="!isPreviewImagePending"
              :src="image"
              :alt="form.title"
              class="img-fluid"
            />
            <div v-else>
              <p class="text-center text-dark">
                <span>{{ $gettext('Generating preview image') }}</span>
              </p>
              <CiLoadingDots
                class="mt-6 flex justify-center"
                size="sm"
              />
            </div>
          </div>
        </div>

        <div class="col-12 col-md-8">
          <input
            v-model="form.embed_param_h"
            type="hidden"
            name="h"
          />

          <InputTextfield
            :id="`video_url_${props.index}`"
            v-model="urlValue"
            :disabled="props.disabled || isUpload"
            :error="!!urlError"
            :label="videoUrlLabel"
            :name="`video_url_${props.index}`"
            :placeholder="videoUrlPlaceholder"
            :required="true"
            inverted
            type="text"
            @blur="getPreview"
          />

          <div
            v-if="!urlValue"
            class="video-upload mb-4"
          >
            <label class="input-label font-medium"><span>{{ $gettext('Or upload your own') }}</span></label>
            <CiDropzone
              v-model="dropzoneValue"
              :name="`video_upload_${props.index}`"
              :dropzone-pending="vimeoUploading || props.disabled"
              :has-error="dropzoneError"
              accept="video/*"
              @ci-dropzone-files-selected="onVideoSelected"
            />
          </div>

          <InputTextfield
            :id="`video_title_${props.index}`"
            v-model="titleValue"
            :disabled="props.disabled"
            :error="!!titleError"
            :label="titleLabel"
            :name="`video_title_${props.index}`"
            :placeholder="titlePlaceholder"
            :required="true"
            inverted
            type="text"
          />

          <InputTextfield
            :id="`video_description_${props.index}`"
            v-model="form.description"
            :label="descriptionLabel"
            :placeholder="descriptionPlaceholder"
            :disabled="props.disabled"
            type="textarea"
            rows="5"
            inverted
          />
        </div>

        <div class="col-12 col-md-1 flex items-center justify-center">
          <div
            v-if="props.showRemoveButton"
            class="pr-4"
          >
            <CiButton
              tag="button"
              variant="danger"
              size="icon"
              :icon="faTrashAlt"
              :disabled="props.disabled"
              @click="emit('remove')"
            >
              <CiAwesomeIcon :icon="faTrashAlt" ratio="0.8" />
            </CiButton>
          </div>
        </div>

        <div class="col-12">
          <CiInfoPanel
            :text="getErrors"
            :condition="getErrors !== ''"
            type="danger"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<style></style>
